import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AppFrame, Card, ProgressBar, StatusBanner } from '../components/ui';
import { fallbackWeather } from '../domain/todayVillage';
import {
  acknowledgeVillageAchievements217,
  buildVillageSummary217,
  normalizeVillageStoryProgress217,
  type VillageSummary217,
} from '../domain/village217';
import { GuideCharacter } from '../reactions/GuideCharacter';
import { fetchVillageSummary217 } from '../services/villageSummaryApi';
import { useAppStore } from '../store/AppStore';

interface VillageHubPreviewProps {
  summary: VillageSummary217;
  childName: string;
  demo?: boolean;
}

const spaces = [
  { id:'igloo', title:'나의 이글루', copy:'36단계 성장과 벽돌 지갑', route:'/village', icon:'🏠' },
  { id:'gallery', title:'퍼즐 갤러리', copy:'완성 장면과 기억 카드', route:'/collection/puzzle', icon:'🧩' },
  { id:'family', title:'가족 게시판', copy:'응원·미션·경제회의', route:'/child/family-expedition', icon:'🏳️' },
  { id:'fridge', title:'냥장고', copy:'수료증·배지·가족 편지', route:'/child/fridge', icon:'🧊' },
  { id:'library', title:'경제도서관', copy:'읽은 기사와 핵심 단어', route:'/child/concepts', icon:'📚' },
] as const;

function spaceUnlocked(summary: VillageSummary217, id: typeof spaces[number]['id']): boolean {
  if (id === 'igloo' || id === 'library') return true;
  if (id === 'gallery') return summary.visual.galleryLit || summary.iglooStage >= 27;
  if (id === 'family') return summary.visual.familyFlagVisible || summary.iglooStage >= 20;
  return summary.iglooStage >= 18;
}

export function VillageHubPreview217({ summary, childName, demo = false }: VillageHubPreviewProps) {
  const todayLabel = summary.todayComplete ? '오늘의 탐험 완료' : '오늘의 탐험이 기다리고 있어요';
  return <>
    <section className={`village-hub-hero-2-17 village-time-${summary.visual.timePhase} weather-${summary.visual.weatherKind}`}>
      <div className="village-hub-sky" aria-hidden="true"><i/><i/><i/></div>
      <div className="village-hub-copy">
        <p className="eyebrow">LIVING SNOW VILLAGE · RELEASE 2.17</p>
        <h1>{childName}의 스노우빌리지</h1>
        <p>{todayLabel}. 오늘의 선택이 마을의 불빛과 이야기를 바꿉니다.</p>
        <div className="village-hub-status-row">
          <span>🏠 이글루 {summary.iglooStage}/36</span>
          <span>🧩 퍼즐 {summary.puzzleRate}%</span>
          <span>👨‍👩‍👧 가족 {summary.familySignalsCompleted}/4</span>
        </div>
      </div>
      <GuideCharacter guide="snows" pose={summary.visual.festivalActive?'celebrate':'guide'} state={summary.visual.festivalActive?'celebrate':'guide'} alt="살아있는 스노우빌리지를 안내하는 스노스" priority />
      <div className={`village-scene-2-17 tier-${summary.visual.iglooTier}`} aria-label={`이글루 ${summary.iglooStage}단계 마을 장면`}>
        <div className="village-mountain"/><div className="village-igloo"><i/><em/></div>
        <span className={`village-family-flag ${summary.visual.familyFlagVisible?'active':''}`}>◆</span>
        <span className={`village-campfire ${summary.visual.campfireLit?'active':''}`}>♨</span>
        <span className={`village-gallery-light ${summary.visual.galleryLit?'active':''}`}>✦</span>
      </div>
    </section>

    <section className="village-today-bridge-2-17">
      <div><small>오늘의 행동 → 마을의 변화</small><h2>{summary.todayComplete ? '오늘의 벽돌과 퍼즐 조각이 마을에 보관됐어요' : '경제 탐험을 완료하면 이글루와 퍼즐이 함께 성장해요'}</h2><p>{summary.todayComplete ? `현재 ${summary.points}P · 누적 벽돌 ${summary.totalBricks}개` : '한 번의 탐험에서 주 보상 1개와 보조 보상 1개만 먼저 보여줘요.'}</p></div>
      {demo
        ? <span className="village-demo-action">{summary.todayComplete?'보관 완료':'탐험 시작'}</span>
        : <Link className="primary-button link-button" to={summary.todayComplete?'/today/reward':'/today/article'}>{summary.todayComplete?'오늘의 보상 보기':'오늘의 탐험 시작'}</Link>}
    </section>

    <section className="village-space-grid-2-17" aria-label="스노우빌리지 공간">
      {spaces.map((space) => {
        const unlocked=spaceUnlocked(summary,space.id);
        const content=<><span>{unlocked?space.icon:'🔒'}</span><div><strong>{space.title}</strong><small>{unlocked?space.copy:'성장하면 열려요'}</small></div><b>{unlocked?'›':'잠김'}</b></>;
        return demo
          ? <article key={space.id} className={unlocked?'unlocked':'locked'}>{content}</article>
          : unlocked
            ? <Link key={space.id} className="unlocked" to={space.route}>{content}</Link>
            : <article key={space.id} className="locked">{content}</article>;
      })}
    </section>

    <section className="village-progress-grid-2-17">
      <Card className="village-next-build-2-17">
        <small>다음 건설 목표</small><h2>{summary.iglooStage >= 36 ? '랜드마크 완성' : `다음 단계까지 벽돌 ${summary.nextIglooRequirement}개`}</h2>
        <ProgressBar value={Math.round(summary.iglooStage/36*100)} />
        <p>이글루의 모든 단계에서 외형·기능·감정 메시지 중 하나가 달라집니다.</p>
        {!demo && <Link to="/village">36단계 로드맵 보기</Link>}
      </Card>
      <Card className="village-family-progress-2-17">
        <small>이번 주 가족 탐험</small><h2>{summary.familySignalsCompleted}/4 신호</h2>
        <div>{Array.from({length:4},(_,index)=><i key={index} className={index<summary.familySignalsCompleted?'done':''}>{index<summary.familySignalsCompleted?'✓':index+1}</i>)}</div>
        <p>{summary.familyRewardClaimed?'가족 벽돌과 퍼즐 조각을 보관했어요.':`신호 ${4-summary.familySignalsCompleted}개를 더 모으면 공동 보상이 열려요.`}</p>
        {!demo && <Link to="/child/family-expedition">가족 탐험대 보기</Link>}
      </Card>
    </section>

    <section className={`village-monthly-story-2-17 ${summary.monthlyStory.completed?'complete':''}`}>
      <div><small>{summary.monthlyStory.phaseLabel}</small><h2>{summary.monthlyStory.chapterTitle}</h2><p>{summary.monthlyStory.chapterCopy}</p></div>
      <div className="village-story-progress-2-17"><strong>{summary.monthlyStory.acquiredPieces}/{summary.monthlyStory.totalPieces}</strong><ProgressBar value={summary.monthlyStory.progressRate}/>{demo?<span>{summary.monthlyStory.nextActionLabel}</span>:<Link to={summary.monthlyStory.nextActionRoute}>{summary.monthlyStory.nextActionLabel} ›</Link>}</div>
    </section>
  </>;
}

function Loading() {
  return <AppFrame title="스노우빌리지" navigation="child"><div className="today-loading" role="status">마을의 오늘 모습을 준비하고 있어요…</div></AppFrame>;
}

export function VillageHubScreen217() {
  const { state, patchState } = useAppStore();
  const [remote, setRemote] = useState<VillageSummary217 | null>(null);
  const weather = useMemo(() => fallbackWeather(new Date(), '서울특별시'), []);
  const local = useMemo(() => state ? buildVillageSummary217(state, weather) : null, [state, weather]);
  useEffect(() => {
    const controller = new AbortController();
    void fetchVillageSummary217('서울특별시', controller.signal).then(setRemote).catch(() => undefined);
    return () => controller.abort();
  }, [state?.todayVillage.updatedAt, state?.familyExpedition.updatedAt]);
  if (!state || !local) return <Loading />;
  const summary = remote ?? local;
  const storyProgress = normalizeVillageStoryProgress217(state.villageStory, state.child.id);
  const unseen = summary.achievements.filter((item) => !storyProgress.acknowledgedAchievementIds.includes(item.id));
  const acknowledge = async () => {
    await patchState({ villageStory:acknowledgeVillageAchievements217(storyProgress,state.child.id,summary.achievements.map((item)=>item.id)) });
  };
  return <AppFrame title="스노우빌리지" navigation="child" screenClass="village-hub-screen-2-17">
    <StatusBanner />
    <VillageHubPreview217 summary={summary} childName={state.child.nickname} />
    <section className="village-achievement-section-2-17">
      <header><div><small>최근 마을 변화</small><h2>행동이 남긴 성장 기록</h2></div>{unseen.length>0&&<button type="button" onClick={()=>void acknowledge()}>새 기록 {unseen.length}개 확인</button>}</header>
      <div>{summary.achievements.length?summary.achievements.map((item)=><Link key={item.id} to={item.route} className={storyProgress.acknowledgedAchievementIds.includes(item.id)?'seen':'new'}><span>{item.icon}</span><div><strong>{item.title}</strong><small>{item.copy}</small></div><b>›</b></Link>):<p>오늘의 경제 탐험을 시작하면 첫 마을 기록이 생겨요.</p>}</div>
    </section>
    <Link className="today-demo-link" to="/demo/2.17">MRM 2.17 통합 데모 체험하기</Link>
  </AppFrame>;
}

function demoSummary(step: number): VillageSummary217 {
  const base: VillageSummary217 = {
    version:'2.17', generatedAt:new Date().toISOString(), todayStatus:'not-started', todayComplete:false, points:125, totalBricks:7,
    iglooStage:7, nextIglooRequirement:3, puzzleRate:25, familySignalsCompleted:0, familySignalsTotal:4, familyRewardClaimed:false,
    monthlyStory:{monthKey:'2026-07',title:'설옹의 오래된 은행',phase:'discovery',phaseLabel:'1주차 · 사건 발견',chapterTitle:'설옹의 오래된 장부를 발견했어요',chapterCopy:'오래된 약속과 저축 기록이 담긴 장부를 발견했어요.',progressRate:25,acquiredPieces:3,totalPieces:12,nextActionLabel:'다음 퍼즐 조각 찾기',nextActionRoute:'/collection/puzzle',completed:false},
    visual:{timePhase:'day',weatherKind:'rain',iglooStage:7,iglooTier:2,familyFlagVisible:false,campfireLit:false,galleryLit:false,festivalActive:false,unlockedSpaces:['기본 보관함']},
    achievements:[],
  };
  if (step>=1) Object.assign(base,{todayStatus:'complete',todayComplete:true,points:148,totalBricks:8,iglooStage:8,nextIglooRequirement:4,puzzleRate:33,achievements:[{id:'demo-today',icon:'🧊',title:'오늘의 경제 탐험 완료',copy:'23P와 얼음 벽돌 1개를 보관했어요.',createdAt:new Date().toISOString(),route:'#'}]});
  if (step>=2) { base.familySignalsCompleted=4;base.familyRewardClaimed=true;base.totalBricks=12;base.iglooStage=12;base.nextIglooRequirement=6;base.visual={...base.visual,iglooStage:12,iglooTier:2,familyFlagVisible:true,campfireLit:true}; }
  if (step>=3) { base.puzzleRate=100;base.iglooStage=30;base.nextIglooRequirement=38;base.monthlyStory={...base.monthlyStory,phase:'reveal',phaseLabel:'4주차 · 이야기 해금',chapterTitle:'설옹의 비밀 금고가 열렸어요',chapterCopy:'완성한 장면과 기억 카드를 퍼즐 갤러리에 보관했어요.',progressRate:100,acquiredPieces:12,completed:true,nextActionLabel:'완성 이야기 보기'};base.visual={...base.visual,iglooStage:30,iglooTier:5,galleryLit:true,familyFlagVisible:true,campfireLit:true,unlockedSpaces:['기본 보관함','냥장고','가족 게시판','퍼즐 갤러리','수료증 전시관']}; }
  if (step>=4) { base.iglooStage=36;base.nextIglooRequirement=0;base.visual={...base.visual,iglooStage:36,iglooTier:6,festivalActive:true,unlockedSpaces:[...base.visual.unlockedSpaces,'마을 광장','스노우빌리지 전망대']}; }
  return base;
}

export function VillageIntegratedDemoScreen217() {
  const [step,setStep]=useState(0);
  const labels=['오늘의 마을','하루의 변화','일주일의 성장','한 달의 이야기','완성된 마을'];
  const summary=demoSummary(step);
  return <AppFrame title="MRM 2.17 통합 데모" backTo="/demo" screenClass="village-demo-screen-2-17">
    <div className="demo-sample-banner">체험용 샘플 데이터 · 실제 계정과 완전 분리</div>
    <section className="village-demo-heading-2-17"><div><small>STEP {step+1} / {labels.length}</small><h1>{labels[step]}</h1><p>실제 화면과 같은 마을 허브 컴포넌트로 하루·주간·월간 변화를 압축 체험합니다.</p></div><strong>{step===0?'발견':step===1?'축적':step===2?'연결':step===3?'해금':'자부심'}</strong></section>
    <VillageHubPreview217 summary={summary} childName="하늘" demo />
    <div className="demo-213-actions"><button type="button" className="secondary-button" onClick={()=>setStep(0)}>초기화</button><button type="button" className="primary-button" disabled={step===labels.length-1} onClick={()=>setStep((value)=>Math.min(labels.length-1,value+1))}>{step===labels.length-1?'체험 완료':'다음 변화'}</button></div>
  </AppFrame>;
}
